import {
  FALLBACK_PUBLIC_PRICING,
  GST_RATE,
  MONTHLY_TIER_AMOUNTS_EXCL,
  PLAN_LIMITS,
  type PlanTier,
} from '../constants/pricingPlans';

export interface PricingEstimatorInput {
  documentsPerMonth: number;
  sites: number;
  users: number;
  needsCompliance: boolean;
}

export interface PricingEstimatorResult {
  recommendedTier: PlanTier;
  monthlyAmountInr: number;
  monthlyAmountInclGst: number;
  overageDocuments: number;
  overageCostInr: number;
  rationale: string;
}

const MONTHLY_EXCL = MONTHLY_TIER_AMOUNTS_EXCL;

const TIER_ORDER: PlanTier[] = ['free', 'starter', 'core', 'growth', 'scale'];

/** Smallest tier that satisfies usage inputs (and compliance when required). */
const pickTier = (input: PricingEstimatorInput): PlanTier => {
  const docs = Math.max(0, input.documentsPerMonth);
  const sites = Math.max(1, input.sites);
  const users = Math.max(1, input.users);

  for (const tier of TIER_ORDER) {
    const limits = PLAN_LIMITS[tier];
    if (input.needsCompliance && !limits.complianceHub) {
      continue;
    }
    const siteCap = limits.sites == null ? Number.MAX_SAFE_INTEGER : limits.sites;
    if (sites > siteCap || users > limits.users || docs > limits.documentsPerMonth) {
      continue;
    }
    return tier;
  }
  return 'scale';
};

/** Suggest a fixed monthly tier from self-serve inputs (excl. GST). */
export function estimateRecommendedPlan(input: PricingEstimatorInput): PricingEstimatorResult {
  const recommendedTier = pickTier(input);
  if (recommendedTier === 'free') {
    return {
      recommendedTier,
      monthlyAmountInr: 0,
      monthlyAmountInclGst: 0,
      overageDocuments: 0,
      overageCostInr: 0,
      rationale: 'Your inputs fit the Free tier for onboarding and limited assessment.',
    };
  }

  const limits = PLAN_LIMITS[recommendedTier];
  const monthlyAmountInr = MONTHLY_EXCL[recommendedTier];
  const docLimit = limits.documentsPerMonth;
  const overageDocuments = Math.max(0, input.documentsPerMonth - docLimit);
  const overagePerDoc =
    FALLBACK_PUBLIC_PRICING.usagePricing.overagePerDocumentInr ?? 12;
  const overageCostInr = overageDocuments * overagePerDoc;

  const siteWord = input.sites === 1 ? 'site' : 'sites';
  const userWord = input.users === 1 ? 'user' : 'users';
  let rationale = `Based on ${input.documentsPerMonth} documents/mo, ${input.sites} ${siteWord}, and ${input.users} ${userWord}.`;
  if (input.needsCompliance) {
    rationale += ' Compliance hub and BRSR exports require Growth or above.';
  }
  if (recommendedTier === 'scale') {
    rationale += ' Scale is billed annually; monthly totals are shown as an equivalent planning view.';
  }
  if (overageDocuments > 0) {
    rationale += ` Estimated overage: ${overageDocuments} docs × ₹${overagePerDoc}.`;
  }

  return {
    recommendedTier,
    monthlyAmountInr: monthlyAmountInr + overageCostInr,
    monthlyAmountInclGst: Math.round((monthlyAmountInr + overageCostInr) * (1 + GST_RATE)),
    overageDocuments,
    overageCostInr,
    rationale,
  };
}
